import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'

import PageContainer from '@/components/ui/containers/PageContainer'

import { StructureActivityCode } from '@/core/models/establishment.model'

import { EstablishmentFormData } from '@/features/establishment'

import { useRequestHandler } from '@/hooks/useRequestHandler'

import { useAppDispatch } from '@/redux/rootReducer'
import { createEstablishment, getEstablishments, selectAdminCreateRequestTracker } from '@/redux/reducers/admin.reducer'

import CreateEstablishmentDialog from './containers/CreateEstablishmentDialog'
import EstablishmentsTable from './containers/EstablishmentsTable'

export default function EstablishmentsPage() {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const [createEstablishmentDialogOpen, setCreateEstablishmentDialogOpen] = useState<boolean>(false)

  const createEstablishmentRequestHandler = useRequestHandler({
    onSuccess: () => {
      enqueueSnackbar(t('notification.establishment.create.success'), { variant: 'success' })
      setCreateEstablishmentDialogOpen(false)
    },
    onError: () => enqueueSnackbar(t('notification.establishment.create.error'), { variant: 'error' }),
    requestTrackerSelector: selectAdminCreateRequestTracker,
  })

  useEffect(() => {
    dispatch(getEstablishments())
  }, [dispatch])

  const handleOpenCreateEstablishmentDialog = () => {
    setCreateEstablishmentDialogOpen(true)
  }

  const handleCloseCreateEstablishmentDialog = () => {
    setCreateEstablishmentDialogOpen(false)
  }

  const handleCreateEstablishment = (values: EstablishmentFormData) => {
    createEstablishmentRequestHandler.dispatch(
      createEstablishment({
        ...values,
        preferredLanguage: values.preferredLanguage || undefined,
        structureActivity: {
          code: values.structureActivity as StructureActivityCode,
        },
      })
    )

    handleCloseCreateEstablishmentDialog()
  }

  return (
    <PageContainer maxWidth="lg">
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={handleOpenCreateEstablishmentDialog} variant="contained" id="create-establishment-button">
            {t('page.superAdmin.createEstablishmentButton.label')}
          </Button>
        </Box>
        <EstablishmentsTable />
        <CreateEstablishmentDialog
          open={createEstablishmentDialogOpen}
          onClose={handleCloseCreateEstablishmentDialog}
          onCreateEstablishment={handleCreateEstablishment}
        />
      </Stack>
    </PageContainer>
  )
}
